import { useCallback, useEffect, useState } from 'react'
import { Avatar, Box, Button, Typography } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
// Localization
import { t } from '$i18n'
// Helper
import { closeSelectWorkspaceColor, switchToRoomList } from '$logic/router'
import { createWorkspace, updateWorkspace } from '$logic/workspace/service'
import { AppBarBackTitle } from '$common/app-bar/app-bar-back-title'
import { ArrowForward, Groups, Search, Settings } from '@mui/icons-material'
import { PrimaryButton } from '$common/buttons'
import { AVATAR_SIZES } from '$common/avatar/avatar-base/conf'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { useWorkspaceById } from '$logic/workspace'
import {
  openDefineWorkspaceColorPagePayload,
  openEditWorkspaceColorPagePayload,
  openWorkspaceColorPagePayload,
} from '$logic/workspace/types'
import { getFileUrl } from '@weblite/file'

export function SelectWorkspaceColor(props: openWorkspaceColorPagePayload) {
  const editMode = !!(props as openEditWorkspaceColorPagePayload).workspaceId
  const name = (props as openDefineWorkspaceColorPagePayload).name
  const icon = (props as openDefineWorkspaceColorPagePayload).icon
  const workspaceId = (props as openEditWorkspaceColorPagePayload).workspaceId

  const { color: defaultColor } =
    useConditionalHook(!!workspaceId, {
      hook: useWorkspaceById,
      hookArgs: workspaceId as string,
      defaultValue: undefined,
    }) || {}

  const [color, setColor] = useState(defaultColor || colors[0])

  useEffect(() => {
    if (!workspaceId || !defaultColor) return
    setColor(defaultColor)
  }, [workspaceId, defaultColor])

  const onConfirmHandle = useCallback(async () => {
    if (!color) return
    // TODO: Show Loading
    if (editMode) {
      await updateWorkspace({ name, color, icon, workspaceId })
      switchToRoomList()
      return
    }
    const newWorkspaceId = await createWorkspace({ name, color, icon })
    if (!newWorkspaceId) return
    analite('SELECT_WORKSPACE_COLOR', {
      color,
      workspaceId: newWorkspaceId,
    })
    switchToRoomList()
  }, [name, color, icon, workspaceId, editMode])

  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitle
          title={t(
            workspaceId
              ? 'selectWorkspaceName.edit'
              : 'selectWorkspaceName.title',
          )}
          onBack={closeSelectWorkspaceColor}
        />
      }
    >
      <Box
        sx={{
          margin: '80px 16px auto 16px',
        }}
      >
        <PreviewWorkspace name={name} color={color} icon={icon} />
      </Box>
      <ButtonWrapper>
        <ColorPalette selectedColor={color} onSelect={setColor} />

        <ButtonRow>
          <Button
            variant="outlined"
            fullWidth
            onClick={closeSelectWorkspaceColor}
          >
            {t('selectWorkspaceColor.back')}
          </Button>
          <PrimaryButton
            fullWidth
            text={t('selectWorkspaceColor.confirm')}
            onClick={onConfirmHandle}
          />
        </ButtonRow>
      </ButtonWrapper>
    </PageWithAppBar>
  )
}

const ButtonWrapper = styled(SettingsSection)`
  margin-bottom: 0;
  margin-top: auto;
  padding: 16px;
  gap: 20px;
`

const ButtonRow = styled('div')`
  display: flex;
  gap: 12px;
`

export function PreviewWorkspace({
  color,
  name,
  icon,
}: {
  color: string
  name: string
  icon: string
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0px 0px 4px 0px #0000001A',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          backgroundColor: color,
          padding: '16px',
          alignItems: 'start',
          justifyContent: 'space-between',
          color: 'white',
          height: '122px',
        }}
      >
        <ArrowForward sx={{ fontSize: 32 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Settings sx={{ fontSize: 32 }} />
          <Search sx={{ fontSize: 32 }} />
        </Box>
      </Box>

      <div
        style={{
          height: '96px',
          padding: '20px 20px 20px',
          alignItems: 'end',
          backgroundColor: 'white',
          display: 'flex',
          gap: '8px',
          position: 'relative',
        }}
      >
        <Groups sx={{ color: 'neutral500' }} />
        <Typography variant="h2">{name}</Typography>
        <StyledAvatar
          src={getFileUrl(icon || '')}
          sx={{ width: AVATAR_SIZES.xl, height: AVATAR_SIZES.xl }}
        >
          <Groups />
        </StyledAvatar>
      </div>
    </Box>
  )
}

const StyledAvatar = styled(Avatar)`
  border: 6px solid ${({ theme }) => theme.palette.neutral000};
  position: absolute;
  left: 14px;
  border-radius: 12px;
  top: -28px;
`

const colors = [
  '#1289c1',
  '#1c59fb',
  '#6B54F7',
  '#9346EB',
  '#9237B7',
  '#D8549A',
  '#C52852',
  '#d94c3e',
  '#e7842c',
  '#dfaa0c',
  '#8db12b',
  '#1f4133',
  '#50585F',
  '#242728',
]

function ColorPalette({
  selectedColor,
  onSelect,
}: {
  selectedColor: string
  onSelect: (color: string) => void
}) {
  const theme = useTheme()
  return (
    <div
      style={{
        width: '100%',
        direction: 'ltr',
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridRowGap: '18px',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.neutral300}`,
        padding: '12px',
      }}
    >
      {colors.map(color => (
        <StyledButton
          key={color}
          onClick={() => onSelect(color)}
          selected={selectedColor === color}
          style={{ backgroundColor: color }}
          aria-label={`Select color ${color}`}
        />
      ))}
    </div>
  )
}

const StyledButton = styled(Button)<{ selected: boolean }>`
  border-radius: 50%;
  width: 32px;
  height: 32px;
  border: 2px solid ${({ theme }) => theme.palette.neutral000};
  outline: ${({ selected, theme }) =>
    selected ? `2px solid ${theme.palette.neutral600}` : 'none'};
  min-width: unset;
  justify-self: center;
`
